import React from 'react'

interface Column<T> {
  key: string
  header: string
  render?: (item: T) => React.ReactNode
  align?: 'left' | 'center' | 'right'
  width?: string
}

interface TableProps<T> {
  columns: Column<T>[]
  data: T[]
  keyExtractor: (item: T) => string
  emptyMessage?: string
  loading?: boolean
  onRowClick?: (item: T) => void
  className?: string
  children?: React.ReactNode
}

export function Table<T>({
  columns,
  data,
  keyExtractor,
  emptyMessage = 'Ei tietoja',
  loading = false,
  onRowClick,
  className = '',
}: TableProps<T>) {
  if (loading) {
    return (
      <div className="p-8 text-center text-gray-500">
        <div className="inline-block w-8 h-8 border-2 border-gray-300 border-t-indigo-600 rounded-full animate-spin" />
        <p className="mt-2">Ladataan...</p>
      </div>
    )
  }

  if (data.length === 0) {
    return (
      <div className="p-8 text-center text-gray-400">
        {emptyMessage}
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className={`w-full ${className}`}>
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className={`
                  px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider
                  ${col.align === 'center' ? 'text-center' : col.align === 'right' ? 'text-right' : 'text-left'}
                `}
                style={{ width: col.width }}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {data.map((item) => (
            <tr
              key={keyExtractor(item)}
              className={`
                hover:bg-gray-50 transition
                ${onRowClick ? 'cursor-pointer' : ''}
              `}
              onClick={() => onRowClick?.(item)}
            >
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={`
                    px-6 py-4 whitespace-nowrap text-sm text-gray-900
                    ${col.align === 'center' ? 'text-center' : col.align === 'right' ? 'text-right' : 'text-left'}
                  `}
                >
                  {col.render ? col.render(item) : (item as Record<string, unknown>)[col.key] as React.ReactNode}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

interface DataTableProps {
  children: React.ReactNode
}

export function DataTable({ children }: DataTableProps) {
  return (
    <div className="bg-white rounded-xl shadow overflow-hidden">
      {children}
    </div>
  )
}

interface TableHeaderProps {
  children: React.ReactNode
}

export function TableHeader({ children }: TableHeaderProps) {
  return (
    <thead className="bg-gray-50 border-b border-gray-200">
      <tr>{children}</tr>
    </thead>
  )
}

interface TableHeadProps {
  children: React.ReactNode
  align?: 'left' | 'center' | 'right'
}

export function TableHead({ children, align = 'left' }: TableHeadProps) {
  return (
    <th
      className={`
        px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider
        ${align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left'}
      `}
    >
      {children}
    </th>
  )
}

interface TableBodyProps {
  children: React.ReactNode
}

export function TableBody({ children }: TableBodyProps) {
  return <tbody className="divide-y divide-gray-200">{children}</tbody>
}

interface TableRowProps {
  children: React.ReactNode
  onClick?: () => void
}

export function TableRow({ children, onClick }: TableRowProps) {
  return (
    <tr
      className={`
        hover:bg-gray-50 transition
        ${onClick ? 'cursor-pointer' : ''}
      `}
      onClick={onClick}
    >
      {children}
    </tr>
  )
}

interface TableCellProps {
  children: React.ReactNode
  align?: 'left' | 'center' | 'right'
  className?: string
}

export function TableCell({ children, align = 'left', className = '' }: TableCellProps) {
  return (
    <td
      className={`
        px-6 py-4 whitespace-nowrap text-sm text-gray-900
        ${align === 'center' ? 'text-center' : align === 'right' ? 'text-right' : 'text-left'}
        ${className}
      `}
    >
      {children}
    </td>
  )
}
